import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LuCheck,
  LuX,
  LuEye,
  LuLoader2,
  LuAlertCircle,
  LuRefreshCw,
  LuTarget,
  LuCode,
  LuChevronDown,
  LuChevronRight
} from 'react-icons/lu';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAllFields, validateFieldMapping } from '../config/moduleSchemas';

const FieldMappingInterface = ({ 
  moduleType, 
  scrapeURL, 
  fieldMappings = {}, 
  onMappingChange,
  onValidationChange 
}) => {
  const [htmlAnalysis, setHtmlAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    required: true,
    optional: false,
    detected: true
  });

  const moduleFields = getAllFields(moduleType);
  const requiredFields = moduleFields.filter(f => f.isRequired);
  const optionalFields = moduleFields.filter(f => !f.isRequired);

  // Simple mock analysis that provides sensible defaults
  const analyzeURL = useCallback(async () => {
    if (!scrapeURL || isAnalyzing) return;

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      // Simulate analysis with realistic selectors for events
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API delay

      const mockAnalysis = {
        htmlAnalysis: {
          title: "Events Page Analysis",
          headings: [
            { tag: "h1", text: "Upcoming Events", classes: "page-title", id: "" },
            { tag: "h2", text: "Featured Events", classes: "section-header", id: "" },
            { tag: "h3", text: "Event Title", classes: "event-title", id: "" }
          ],
          forms: [],
          tables: [],
          lists: [
            { type: "ul", itemCount: 10, classes: "events-list", id: "events-container" }
          ],
          structuralElements: [
            { tag: "div", classes: "event-item", id: "", childCount: 5 },
            { tag: "div", classes: "event-details", id: "", childCount: 3 },
            { tag: "div", classes: "event-meta", id: "", childCount: 2 }
          ]
        },
        scrapingInstructions: {
          scrapingStrategy: "content_parsing",
          fieldMappings: {
            title: ".event-title, h3.event-name",
            description: ".event-description, .event-details p",
            time: ".event-date, .event-time, time",
            location: ".event-location, .venue",
            photoURL: ".event-image img, .event-photo img",
            organizer: ".event-organizer, .host",
            siteURL: ".event-link a, .more-info a",
            endTime: ".event-end-time, .end-date",
            category: ".event-category, .event-type"
          },
          targetSelectors: [".event-item", ".event-card", ".calendar-event"],
          recommendedTools: ["BeautifulSoup", "Cheerio", "Puppeteer"]
        }
      };

      setHtmlAnalysis(mockAnalysis);
    } catch (error) {
      console.error('Error analyzing URL:', error);
      setAnalysisError('Analysis failed. Using default selectors.');
    } finally {
      setIsAnalyzing(false);
    }
  }, [scrapeURL, moduleType, moduleFields, isAnalyzing]);

  // Check if URL is ready for analysis
  const isReadyForAnalysis = scrapeURL && 
    scrapeURL.trim() && 
    scrapeURL.startsWith('http') && 
    !isAnalyzing;

  // Use ref to track previous validation to prevent infinite loops
  const prevValidationRef = useRef(null);

  // Validate mappings when they change (use ref to prevent infinite loops)
  useEffect(() => {
    if (moduleType) {
      const validation = validateFieldMapping(moduleType, fieldMappings);
      const validationString = JSON.stringify(validation);
      
      // Only call onValidationChange if validation actually changed
      if (prevValidationRef.current !== validationString) {
        prevValidationRef.current = validationString;
        if (onValidationChange) {
          onValidationChange(validation);
        }
      }
    }
  }, [fieldMappings, moduleType, onValidationChange]);

  const handleFieldMapping = useCallback((fieldKey, selector) => {
    const newMappings = {
      ...fieldMappings,
      [fieldKey]: selector
    };
    onMappingChange?.(newMappings);
  }, [fieldMappings, onMappingChange]);

  const clearMapping = useCallback((fieldKey) => {
    const newMappings = { ...fieldMappings };
    delete newMappings[fieldKey];
    onMappingChange?.(newMappings);
  }, [fieldMappings, onMappingChange]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const getSuggestedSelectors = useCallback((field) => {
    if (!htmlAnalysis) return [];

    const suggestions = [];
    
    // First, check if the HTML parser provided specific field mappings
    if (htmlAnalysis.scrapingInstructions?.fieldMappings && 
        htmlAnalysis.scrapingInstructions.fieldMappings[field.key]) {
      suggestions.push({
        selector: htmlAnalysis.scrapingInstructions.fieldMappings[field.key],
        description: `AI-suggested mapping for ${field.label}`,
        priority: 'high'
      });
    }

    const { htmlAnalysis: analysis } = htmlAnalysis;

    // Look for relevant selectors based on field type and name
    switch (field.key) {
      case 'title':
      case 'headline':
        // Look for headings first
        analysis.headings?.forEach(heading => {
          if (heading.classes) {
            suggestions.push({
              selector: `.${heading.classes.split(' ')[0]}`,
              description: `${heading.tag.toUpperCase()}: "${heading.text.substring(0, 50)}..."`
            });
          }
          if (heading.id) {
            suggestions.push({
              selector: `#${heading.id}`,
              description: `${heading.tag.toUpperCase()} by ID: "${heading.text.substring(0, 50)}..."`
            });
          }
        });
        break;

      case 'time':
      case 'date':
      case 'publishDate':
        // Look for time-related elements
        analysis.structuralElements?.forEach(element => {
          if (element.classes && (
            element.classes.includes('date') || 
            element.classes.includes('time') ||
            element.classes.includes('datetime')
          )) {
            suggestions.push({
              selector: `.${element.classes.split(' ')[0]}`,
              description: `Date/Time element (${element.tag})`
            });
          }
        });
        break;

      case 'description':
      case 'content':
        // Look for content areas
        analysis.structuralElements?.forEach(element => {
          if (element.classes && (
            element.classes.includes('content') ||
            element.classes.includes('description') ||
            element.classes.includes('body')
          )) {
            suggestions.push({
              selector: `.${element.classes.split(' ')[0]}`,
              description: `Content area (${element.tag})`
            });
          }
        });
        break;

      case 'photoURL':
      case 'imageURL':
        // Look for images
        suggestions.push({
          selector: 'img',
          description: 'All images'
        });
        break;

      default:
        // Generic suggestions based on common class patterns
        analysis.structuralElements?.forEach(element => {
          if (element.classes && element.classes.toLowerCase().includes(field.key.toLowerCase())) {
            suggestions.push({
              selector: `.${element.classes.split(' ')[0]}`,
              description: `Element matching "${field.key}" (${element.tag})`
            });
          }
        });
    }

    // Remove duplicates and prioritize AI suggestions
    const uniqueSuggestions = suggestions.filter((item, index, self) => 
      index === self.findIndex(t => t.selector === item.selector)
    );

    // Sort by priority (AI suggestions first)
    uniqueSuggestions.sort((a, b) => {
      if (a.priority === 'high' && b.priority !== 'high') return -1;
      if (b.priority === 'high' && a.priority !== 'high') return 1;
      return 0;
    });

    return uniqueSuggestions.slice(0, 4); // Limit to top 4 suggestions
  }, [htmlAnalysis]);

  return (
    <div className="space-y-6">
      {/* Analysis Status */}
      <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-medium flex items-center gap-2">
            <LuTarget className="w-4 h-4" />
            HTML Structure Analysis
          </h4>
          <button
            onClick={analyzeURL}
            disabled={!isReadyForAnalysis}
            className={`px-3 py-1 text-sm border rounded transition-colors flex items-center gap-2 ${
              isReadyForAnalysis
                ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                : isAnalyzing
                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                : 'bg-gray-500/20 text-gray-400 border-gray-500/30 cursor-not-allowed opacity-50'
            }`}
          >
            {isAnalyzing ? (
              <>
                <LuLoader2 className="w-3 h-3 animate-spin" />
                Analyzing...
              </>
            ) : isReadyForAnalysis ? (
              <>
                <LuRefreshCw className="w-3 h-3" />
                Analyze URL
              </>
            ) : (
              <>
                <LuRefreshCw className="w-3 h-3" />
                Enter URL to Analyze
              </>
            )}
          </button>
        </div>

        {isReadyForAnalysis && !htmlAnalysis && !isAnalyzing && (
          <div className="mb-3 p-3 bg-green-500/10 border border-green-500/20 rounded flex items-center gap-2">
            <LuCheck className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm">
              Ready to analyze! Click "Analyze URL" to get field mapping suggestions.
            </span>
          </div>
        )}

        {analysisError && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded flex items-center gap-2">
            <LuAlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm">{analysisError}</span>
          </div>
        )}

        {htmlAnalysis && (
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="text-xl font-bold text-white">{htmlAnalysis.htmlAnalysis?.headings?.length || 0}</div>
              <div className="text-white/60">Headings</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-white">{htmlAnalysis.htmlAnalysis?.forms?.length || 0}</div>
              <div className="text-white/60">Forms</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-white">{htmlAnalysis.htmlAnalysis?.tables?.length || 0}</div>
              <div className="text-white/60">Tables</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-white">{htmlAnalysis.htmlAnalysis?.lists?.length || 0}</div>
              <div className="text-white/60">Lists</div>
            </div>
          </div>
        )}
      </div>

      {/* Required Fields */}
      <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
        <button
          onClick={() => toggleSection('required')}
          className="w-full flex items-center justify-between text-white font-medium mb-3"
        >
          <div className="flex items-center gap-2">
            <LuAlertCircle className="w-4 h-4 text-red-400" />
            Required Fields ({requiredFields.length})
          </div>
          {expandedSections.required ? <LuChevronDown className="w-4 h-4" /> : <LuChevronRight className="w-4 h-4" />}
        </button>

        {expandedSections.required && (
          <div className="space-y-4">
            {requiredFields.map(field => (
              <FieldMappingRow
                key={field.key}
                field={field}
                mapping={fieldMappings[field.key] || ''}
                suggestions={getSuggestedSelectors(field)}
                onMappingChange={(selector) => handleFieldMapping(field.key, selector)}
                onClearMapping={() => clearMapping(field.key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Optional Fields */}
      <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
        <button
          onClick={() => toggleSection('optional')}
          className="w-full flex items-center justify-between text-white font-medium mb-3"
        >
          <div className="flex items-center gap-2">
            <LuCheck className="w-4 h-4 text-green-400" />
            Optional Fields ({optionalFields.length})
          </div>
          {expandedSections.optional ? <LuChevronDown className="w-4 h-4" /> : <LuChevronRight className="w-4 h-4" />}
        </button>

        {expandedSections.optional && (
          <div className="space-y-4">
            {optionalFields.map(field => (
              <FieldMappingRow
                key={field.key}
                field={field}
                mapping={fieldMappings[field.key] || ''}
                suggestions={getSuggestedSelectors(field)}
                onMappingChange={(selector) => handleFieldMapping(field.key, selector)}
                onClearMapping={() => clearMapping(field.key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detected Elements Preview */}
      {htmlAnalysis && (
        <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded-lg">
          <button
            onClick={() => toggleSection('detected')}
            className="w-full flex items-center justify-between text-white font-medium mb-3"
          >
            <div className="flex items-center gap-2">
              <LuCode className="w-4 h-4 text-blue-400" />
              Detected Elements
            </div>
            {expandedSections.detected ? <LuChevronDown className="w-4 h-4" /> : <LuChevronRight className="w-4 h-4" />}
          </button>

          {expandedSections.detected && (
            <div className="text-sm text-white/80">
              <div className="mb-2">
                <strong>Strategy:</strong> {htmlAnalysis.scrapingInstructions?.scrapingStrategy}
              </div>
              {htmlAnalysis.scrapingInstructions?.targetSelectors && (
                <div>
                  <strong>Suggested Selectors:</strong>
                  <ul className="list-disc list-inside mt-1 text-white/60">
                    {Array.isArray(htmlAnalysis.scrapingInstructions.targetSelectors) 
                      ? htmlAnalysis.scrapingInstructions.targetSelectors.map((selector, i) => (
                          <li key={i}><code className="bg-[#ffffff10] px-1 rounded">{selector}</code></li>
                        ))
                      : <li><code className="bg-[#ffffff10] px-1 rounded">{htmlAnalysis.scrapingInstructions.targetSelectors}</code></li>
                    }
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FieldMappingRow = ({ field, mapping, suggestions, onMappingChange, onClearMapping }) => {
  const [showSuggestions, setShowSuggestions] = useState(true); // Show suggestions by default

  // Find the best suggestion (AI suggestion if available)
  const bestSuggestion = suggestions.find(s => s.priority === 'high') || suggestions[0];
  const currentMapping = mapping || bestSuggestion?.selector || '';

  return (
    <div className="p-3 bg-[#ffffff08] border border-[#ffffff10] rounded">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">{field.label}</span>
          {field.isRequired && <span className="text-red-400 text-sm">*</span>}
          <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
            {field.type}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {currentMapping && (
            <div className="flex items-center gap-1">
              <LuCheck className="w-3 h-3 text-green-400" />
              <span className="text-green-400 text-xs">Mapped</span>
            </div>
          )}
        </div>
      </div>

      <p className="text-white/60 text-sm mb-3">{field.description}</p>

      {/* Display current mapping (read-only) */}
      {currentMapping ? (
        <div className="mb-3">
          <div className="text-white/80 text-sm mb-1">Detected Selector:</div>
          <div className="px-3 py-2 bg-[#ffffff05] border border-[#ffffff15] rounded text-white text-sm">
            <code className="bg-[#ffffff10] px-1 rounded">{currentMapping}</code>
          </div>
        </div>
      ) : (
        <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-sm">
          No selector detected. Run analysis to get suggestions.
        </div>
      )}

      {/* Show all available suggestions */}
      {suggestions.length > 0 && (
        <div>
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="text-blue-400 text-sm hover:text-blue-300 transition-colors flex items-center gap-1 mb-2"
          >
            <LuEye className="w-3 h-3" />
            {suggestions.length} available selector{suggestions.length !== 1 ? 's' : ''}
            {showSuggestions ? <LuChevronDown className="w-3 h-3" /> : <LuChevronRight className="w-3 h-3" />}
          </button>

          {showSuggestions && (
            <div className="space-y-1">
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => onMappingChange(suggestion.selector)}
                  className={`w-full text-left p-2 hover:bg-[#ffffff10] border rounded text-sm transition-colors ${
                    suggestion.priority === 'high' 
                      ? 'bg-blue-500/10 border-blue-500/30' 
                      : 'bg-[#ffffff05] border-[#ffffff10]'
                  } ${
                    currentMapping === suggestion.selector ? 'ring-2 ring-green-500/50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-white/80">
                      <code className="bg-[#ffffff10] px-1 rounded">{suggestion.selector}</code>
                    </div>
                    {suggestion.priority === 'high' && (
                      <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
                        AI Recommended
                      </span>
                    )}
                    {currentMapping === suggestion.selector && (
                      <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 border border-green-500/30 rounded">
                        Selected
                      </span>
                    )}
                  </div>
                  <div className="text-white/50 text-xs mt-1">{suggestion.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldMappingInterface;